import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
} from 'reactstrap';
import DataTable from '../../components/DataTable';
import ConfirmModal from '../../components/ConfirmModal';
import Loading from '../../components/Loading';
import UsuarioForm from './UsuarioForm';
import { usuariosService } from '../../services/usuariosService';
import { empresasService } from '../../services/empresasService';
import SnowFallComponent from 'components/SnowFallComponent';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [usuarioToDelete, setUsuarioToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadEmpresa();
  }, []);

  useEffect(() => {
    if (empresa) {
      loadUsuarios();
    }
  }, [empresa]);

  const loadEmpresa = async () => {
    try {
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        setEmpresa(empresaActiva);
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    }
  };

  // Función auxiliar para verificar si un usuario es administrador
  const esAdministrador = (usuario) => {
    if (!usuario) return false;
    const roles = usuario.Roles || usuario.roles || [];
    if (!Array.isArray(roles) || roles.length === 0) {
      return false;
    }

    const esAdmin = roles.some(rol => {
      const nombreRol = rol?.Nombre || rol?.nombre || '';
      return nombreRol.toLowerCase() === 'administrador';
    });

    return esAdmin;
  };

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      setError('');
      if (!empresa) return;

      const response = await usuariosService.getAll(empresa.EmpresaID || empresa.empresaID);

      if (response.success && response.data) {
        const usuariosData = Array.isArray(response.data) ? response.data : [];
        const normalized = usuariosData.map(usuario => {
          const roles = usuario.Roles || usuario.roles || [];
          // Log temporal para depurar
          if (roles.length > 0) {
            console.log(`Usuario ${usuario.Nombre || usuario.nombre}:`, roles);
          }
          return {
            UsuarioID: usuario.UsuarioID || usuario.usuarioID,
            EmpresaID: usuario.EmpresaID || usuario.empresaID,
            Nombre: usuario.Nombre || usuario.nombre || '',
            Correo: usuario.Correo || usuario.correo || '',
            Activo: usuario.Activo !== undefined ? usuario.Activo : (usuario.activo !== undefined ? usuario.activo : true),
            FechaCreacion: usuario.FechaCreacion || usuario.fechaCreacion,
            FechaUltimoAcceso: usuario.FechaUltimoAcceso || usuario.fechaUltimoAcceso,
            EmpresaNombre: usuario.EmpresaNombre || usuario.empresaNombre,
            Roles: roles, // Incluir roles para verificar si es administrador
          };
        });
        setUsuarios(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar usuarios');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUsuario(null);
    setShowForm(true);
  };

  const handleEdit = async (usuario) => {
    // Verificar si el usuario es administrador
    if (esAdministrador(usuario)) {
      setError('No se puede editar un usuario administrador. Los administradores no pueden ser modificados desde esta interfaz.');
      return;
    }

    try {
      setLoading(true);
      const response = await usuariosService.getById(usuario.UsuarioID || usuario.usuarioID);
      if (response.success && response.data) {
        setEditingUsuario(response.data);
        setShowForm(true);
      } else {
        setError(response.Message || response.message || 'Error al cargar el usuario');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (usuario) => {
    // Verificar si el usuario es administrador
    if (esAdministrador(usuario)) {
      setError('No se puede eliminar un usuario administrador. Los administradores no pueden ser eliminados desde esta interfaz.');
      return;
    }

    setUsuarioToDelete(usuario);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!usuarioToDelete) return;

    // Verificación adicional antes de eliminar
    if (esAdministrador(usuarioToDelete)) {
      setError('No se puede eliminar un usuario administrador.');
      setShowDeleteModal(false);
      setUsuarioToDelete(null);
      return;
    }

    try {
      setDeleting(true);
      const usuarioId = usuarioToDelete.UsuarioID || usuarioToDelete.usuarioID;
      const response = await usuariosService.delete(usuarioId);

      if (response.success) {
        setSuccess('Usuario desactivado exitosamente');
        setTimeout(() => setSuccess(''), 3000);
        setShowDeleteModal(false);
        setUsuarioToDelete(null);
        loadUsuarios();
      } else {
        setError(response.Message || response.message || 'Error al desactivar el usuario');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingUsuario(null);
    setSuccess('Usuario guardado exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    loadUsuarios();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingUsuario(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      key: 'nombre',
      label: 'Nombre',
      accessor: 'Nombre',
    },
    {
      key: 'correo',
      label: 'Correo',
      accessor: 'Correo',
    },
    {
      key: 'empresa',
      label: 'Empresa',
      accessor: 'EmpresaNombre',
      render: (value) => value || '-',
    },
    {
      key: 'activo',
      label: 'Estado',
      accessor: 'Activo',
      render: (value) => (
        <Badge color={value ? 'success' : 'danger'}>
          {value ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
    {
      key: 'fechaCreacion',
      label: 'Fecha Creación',
      accessor: 'FechaCreacion',
      render: (value) => formatDate(value),
    },
    {
      key: 'fechaUltimoAcceso',
      label: 'Último Acceso',
      accessor: 'FechaUltimoAcceso',
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, row) => {
        const esAdmin = esAdministrador(row);
        // No mostrar botones de editar ni eliminar para administradores
        if (esAdmin) {
          return <span className="text-muted"></span>;
        }

        return (
          <div>
            <Button
              color="info"
              size="sm"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              title="Editar"
            >
              <i className="tim-icons icon-pencil" />
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
              title="Desactivar"
              disabled={!row.Activo}
            >
              <i className="tim-icons icon-simple-remove" />
            </Button>
          </div>
        );
      },
    },
  ];

  if (showForm) {
    return (
      <UsuarioForm
        usuario={editingUsuario}
        empresaId={empresa?.EmpresaID || empresa?.empresaID}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Usuarios</CardTitle>
                </Col>
                <Col className="text-right">
                  <Button color="primary" onClick={handleCreate} disabled={!empresa}>
                    <i className="tim-icons icon-simple-add" /> Nuevo Usuario
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              {loading ? (
                <Loading />
              ) : (
                <DataTable columns={columns} data={usuarios} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setUsuarioToDelete(null);
        }}
        title="Desactivar Usuario"
        message="¿Está seguro de desactivar este usuario? El usuario no podrá iniciar sesión pero sus datos se mantendrán."
        confirmText="Desactivar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}

export default Usuarios;

